import {
	makeProperties,
	Material,
} from "@animech/axiom-format/src/definitions/definition-bindings";
import { hiddenSlot, TOTAL_CHAIR_SLOTS } from "./chair-data";
import {
	ChairSlot,
	MaterialTheme,
	materialThemeUniverse,
	seatingTypeUniverse,
	TableType,
	ViewerMode,
	viewerModeUniverse,
} from "./type-definitions";

export type Root = {
	uiSection: string | undefined;
	materialTheme: MaterialTheme;
	tableType: TableType;

	rectControlsVisible: boolean;
	roundControlsVisible: boolean;
	chair: ChairSlot[];

	tableMaterialWood: Material | undefined;
	tableMaterialWood2: Material | undefined;
	tableMaterialMetal: Material;
	tableMaterialWeld: Material;
	tableMaterialEndcap: Material | undefined;
	tableMaterialScrewAtlas: Material;
	tableMaterialScrewPhillips: Material;

	tableTheme: MaterialTheme;

	materialThemeDiverged: boolean;

	areaWidth: number;
	areaDepth: number;
	flipArea: boolean;
	stockVersion: number;

	viewerMode: ViewerMode;
	tableVisible: boolean;
};

/* eslint-disable @animech/axiom/no-references-in-top-level-values */
export const root = makeProperties({
	uiSection: { $state: "" },
	materialTheme: {
		$state: "theme-teak-galvanized",
		$universe: materialThemeUniverse,
	},
	materialThemeDiverged: { $state: false },
	tableType: {
		$state: "Table-B31-170",
		$universe: [
			"Table-Bovik-150",
			"Table-B31-84",
			"Table-B31-170",
			"Table-B31-230",
			"Table-6B-60",
			"Table-9A-100",
			"Table-9A-120",
			"Table-B25A-120",
			"Table-HighTech-60",
			"Table-HighTech-110",
			"Table-Libelle-70",
			"Table-Libelle-140",
		],
	},
	rectControlsVisible: { $state: true },
	roundControlsVisible: { $state: false },
	chair: {
		// Pad chair.$state to TOTAL_CHAIR_SLOTS so per-element paths exist before
		// the first cascade fires. Required by the chair.$ template rules
		// (chairMaterialMetalDerived, chairMaterialWoodDerived) and avoids
		// Axiom's whole-array-vs-template-rule conflict — see CLAUDE.md.
		$state: new Array(TOTAL_CHAIR_SLOTS).fill(hiddenSlot) as ChairSlot[],
		$: {
			visible: { $state: false },
			chairType: {
				$state: "Chair-A2",
				$universe: seatingTypeUniverse,
			},
			pos: {
				x: { $state: 0, $universe: { min: -3, max: 3 } },
				y: { $state: 0, $universe: { min: -3, max: 3 } },
				z: { $state: 0, $universe: { min: -3, max: 3 } },
			},
			rot: {
				x: { $state: 0, $universe: { min: -360, max: 360 } },
				y: { $state: 0, $universe: { min: -360, max: 360 } },
				z: { $state: 0, $universe: { min: -360, max: 360 } },
			},
			chairMaterialWood: {
				$state: undefined,
				$universe: [
					undefined,
					"WoodPineOiled",
					"WoodPineOiled-Endcap",
					"WoodOakOiled",
					"WoodOakOiled-Endcap",
					"WoodTeakUntreated",
					"WoodTeakUntreated-Endcap",
					"LacquerWhite",
					"LacquerGrey",
					"LacquerGraphite",
					"LacquerBlack",
					"PaintWoodWhite",
					"PaintWoodWhite-Endcap",
					"PaintWoodGrey",
					"PaintWoodGrey-Endcap",
					"PaintWoodGraphite",
					"PaintWoodGraphite-Endcap",
					"PaintWoodBlack",
					"PaintWoodBlack-Endcap",
					"PaintWoodRed",
					"PaintWoodRed-Endcap",
					"PaintWoodGreen",
					"PaintWoodGreen-Endcap",
					"LaminateWhite",
					"LaminateGrey",
					"LaminateGraphite",
					"LaminateBlack",
					"PaintMetalWhite",
					"PaintMetalWhite-Weld",
					"PaintMetalGrey",
					"PaintMetalGrey-Weld",
					"PaintMetalGraphite",
					"PaintMetalGraphite-Weld",
					"PaintMetalBlack",
					"PaintMetalBlack-Weld",
					"PaintMetalGreen",
					"PaintMetalGreen-Weld",
					"PaintMetalRed",
					"PaintMetalRed-Weld",
					"PaintMetalPowdercoatBlack",
					"PaintMetalPowdercoatBlack-Weld",
					"PaintMetalPowdercoatChestnut",
					"PaintMetalPowdercoatChestnut-Weld",
					"PaintMetalPowdercoatIvory",
					"PaintMetalPowdercoatIvory-Weld",
					"PaintMetalPowdercoatPetrol",
					"PaintMetalPowdercoatPetrol-Weld",
					"MetalGalvanizedSteel",
					"MetalGalvanizedSteel-Weld",
					"MetalGalvanizedZinc",
					"MetalGalvanizedZinc-Weld",
					"AtlasScrews",
					"AtlasScrews-Black",
					"ScrewRoundedPhillips-M3x10mm-A",
					"ScrewRoundedPhillips-M3x10mm-A-Black",
				],
			},
			chairMaterialMetal: {
				$state: "WoodPineOiled",
				$universe: [
					"WoodPineOiled",
					"WoodPineOiled-Endcap",
					"WoodOakOiled",
					"WoodOakOiled-Endcap",
					"WoodTeakUntreated",
					"WoodTeakUntreated-Endcap",
					"LacquerWhite",
					"LacquerGrey",
					"LacquerGraphite",
					"LacquerBlack",
					"PaintWoodWhite",
					"PaintWoodWhite-Endcap",
					"PaintWoodGrey",
					"PaintWoodGrey-Endcap",
					"PaintWoodGraphite",
					"PaintWoodGraphite-Endcap",
					"PaintWoodBlack",
					"PaintWoodBlack-Endcap",
					"PaintWoodRed",
					"PaintWoodRed-Endcap",
					"PaintWoodGreen",
					"PaintWoodGreen-Endcap",
					"LaminateWhite",
					"LaminateGrey",
					"LaminateGraphite",
					"LaminateBlack",
					"PaintMetalWhite",
					"PaintMetalWhite-Weld",
					"PaintMetalGrey",
					"PaintMetalGrey-Weld",
					"PaintMetalGraphite",
					"PaintMetalGraphite-Weld",
					"PaintMetalBlack",
					"PaintMetalBlack-Weld",
					"PaintMetalGreen",
					"PaintMetalGreen-Weld",
					"PaintMetalRed",
					"PaintMetalRed-Weld",
					"PaintMetalPowdercoatBlack",
					"PaintMetalPowdercoatBlack-Weld",
					"PaintMetalPowdercoatChestnut",
					"PaintMetalPowdercoatChestnut-Weld",
					"PaintMetalPowdercoatIvory",
					"PaintMetalPowdercoatIvory-Weld",
					"PaintMetalPowdercoatPetrol",
					"PaintMetalPowdercoatPetrol-Weld",
					"MetalGalvanizedSteel",
					"MetalGalvanizedSteel-Weld",
					"MetalGalvanizedZinc",
					"MetalGalvanizedZinc-Weld",
					"AtlasScrews",
					"AtlasScrews-Black",
					"ScrewRoundedPhillips-M3x10mm-A",
					"ScrewRoundedPhillips-M3x10mm-A-Black",
				],
			},
			chairMaterialWeld: {
				$state: "WoodPineOiled",
				$universe: [
					"WoodPineOiled",
					"WoodPineOiled-Endcap",
					"WoodOakOiled",
					"WoodOakOiled-Endcap",
					"WoodTeakUntreated",
					"WoodTeakUntreated-Endcap",
					"LacquerWhite",
					"LacquerGrey",
					"LacquerGraphite",
					"LacquerBlack",
					"PaintWoodWhite",
					"PaintWoodWhite-Endcap",
					"PaintWoodGrey",
					"PaintWoodGrey-Endcap",
					"PaintWoodGraphite",
					"PaintWoodGraphite-Endcap",
					"PaintWoodBlack",
					"PaintWoodBlack-Endcap",
					"PaintWoodRed",
					"PaintWoodRed-Endcap",
					"PaintWoodGreen",
					"PaintWoodGreen-Endcap",
					"LaminateWhite",
					"LaminateGrey",
					"LaminateGraphite",
					"LaminateBlack",
					"PaintMetalWhite",
					"PaintMetalWhite-Weld",
					"PaintMetalGrey",
					"PaintMetalGrey-Weld",
					"PaintMetalGraphite",
					"PaintMetalGraphite-Weld",
					"PaintMetalBlack",
					"PaintMetalBlack-Weld",
					"PaintMetalGreen",
					"PaintMetalGreen-Weld",
					"PaintMetalRed",
					"PaintMetalRed-Weld",
					"PaintMetalPowdercoatBlack",
					"PaintMetalPowdercoatBlack-Weld",
					"PaintMetalPowdercoatChestnut",
					"PaintMetalPowdercoatChestnut-Weld",
					"PaintMetalPowdercoatIvory",
					"PaintMetalPowdercoatIvory-Weld",
					"PaintMetalPowdercoatPetrol",
					"PaintMetalPowdercoatPetrol-Weld",
					"MetalGalvanizedSteel",
					"MetalGalvanizedSteel-Weld",
					"MetalGalvanizedZinc",
					"MetalGalvanizedZinc-Weld",
					"AtlasScrews",
					"AtlasScrews-Black",
					"ScrewRoundedPhillips-M3x10mm-A",
					"ScrewRoundedPhillips-M3x10mm-A-Black",
				],
			},
			chairMaterialEndcap: {
				$state: "WoodPineOiled",
				$universe: [
					"WoodPineOiled",
					"WoodPineOiled-Endcap",
					"WoodOakOiled",
					"WoodOakOiled-Endcap",
					"WoodTeakUntreated",
					"WoodTeakUntreated-Endcap",
					"LacquerWhite",
					"LacquerGrey",
					"LacquerGraphite",
					"LacquerBlack",
					"PaintWoodWhite",
					"PaintWoodWhite-Endcap",
					"PaintWoodGrey",
					"PaintWoodGrey-Endcap",
					"PaintWoodGraphite",
					"PaintWoodGraphite-Endcap",
					"PaintWoodBlack",
					"PaintWoodBlack-Endcap",
					"PaintWoodRed",
					"PaintWoodRed-Endcap",
					"PaintWoodGreen",
					"PaintWoodGreen-Endcap",
					"LaminateWhite",
					"LaminateGrey",
					"LaminateGraphite",
					"LaminateBlack",
					"PaintMetalWhite",
					"PaintMetalWhite-Weld",
					"PaintMetalGrey",
					"PaintMetalGrey-Weld",
					"PaintMetalGraphite",
					"PaintMetalGraphite-Weld",
					"PaintMetalBlack",
					"PaintMetalBlack-Weld",
					"PaintMetalGreen",
					"PaintMetalGreen-Weld",
					"PaintMetalRed",
					"PaintMetalRed-Weld",
					"PaintMetalPowdercoatBlack",
					"PaintMetalPowdercoatBlack-Weld",
					"PaintMetalPowdercoatChestnut",
					"PaintMetalPowdercoatChestnut-Weld",
					"PaintMetalPowdercoatIvory",
					"PaintMetalPowdercoatIvory-Weld",
					"PaintMetalPowdercoatPetrol",
					"PaintMetalPowdercoatPetrol-Weld",
					"MetalGalvanizedSteel",
					"MetalGalvanizedSteel-Weld",
					"MetalGalvanizedZinc",
					"MetalGalvanizedZinc-Weld",
					undefined,
					"AtlasScrews",
					"AtlasScrews-Black",
					"ScrewRoundedPhillips-M3x10mm-A",
					"ScrewRoundedPhillips-M3x10mm-A-Black",
				],
			},
			chairMaterialScrewAtlas: {
				$state: "AtlasScrews",
				$universe: [
					"AtlasScrews",
					"AtlasScrews-Black",
					"WoodPineOiled",
					"WoodPineOiled-Endcap",
					"WoodOakOiled",
					"WoodOakOiled-Endcap",
					"WoodTeakUntreated",
					"WoodTeakUntreated-Endcap",
					"LacquerWhite",
					"LacquerGrey",
					"LacquerGraphite",
					"LacquerBlack",
					"PaintWoodWhite",
					"PaintWoodWhite-Endcap",
					"PaintWoodGrey",
					"PaintWoodGrey-Endcap",
					"PaintWoodGraphite",
					"PaintWoodGraphite-Endcap",
					"PaintWoodBlack",
					"PaintWoodBlack-Endcap",
					"PaintWoodRed",
					"PaintWoodRed-Endcap",
					"PaintWoodGreen",
					"PaintWoodGreen-Endcap",
					"LaminateWhite",
					"LaminateGrey",
					"LaminateGraphite",
					"LaminateBlack",
					"PaintMetalWhite",
					"PaintMetalWhite-Weld",
					"PaintMetalGrey",
					"PaintMetalGrey-Weld",
					"PaintMetalGraphite",
					"PaintMetalGraphite-Weld",
					"PaintMetalBlack",
					"PaintMetalBlack-Weld",
					"PaintMetalGreen",
					"PaintMetalGreen-Weld",
					"PaintMetalRed",
					"PaintMetalRed-Weld",
					"PaintMetalPowdercoatBlack",
					"PaintMetalPowdercoatBlack-Weld",
					"PaintMetalPowdercoatChestnut",
					"PaintMetalPowdercoatChestnut-Weld",
					"PaintMetalPowdercoatIvory",
					"PaintMetalPowdercoatIvory-Weld",
					"PaintMetalPowdercoatPetrol",
					"PaintMetalPowdercoatPetrol-Weld",
					"MetalGalvanizedSteel",
					"MetalGalvanizedSteel-Weld",
					"MetalGalvanizedZinc",
					"MetalGalvanizedZinc-Weld",
					"ScrewRoundedPhillips-M3x10mm-A",
					"ScrewRoundedPhillips-M3x10mm-A-Black",
				],
			},
			chairMaterialScrewPhillips: {
				$state: "ScrewRoundedPhillips-M3x10mm-A",
				$universe: [
					"ScrewRoundedPhillips-M3x10mm-A",
					"ScrewRoundedPhillips-M3x10mm-A-Black",
					"WoodPineOiled",
					"WoodPineOiled-Endcap",
					"WoodOakOiled",
					"WoodOakOiled-Endcap",
					"WoodTeakUntreated",
					"WoodTeakUntreated-Endcap",
					"LacquerWhite",
					"LacquerGrey",
					"LacquerGraphite",
					"LacquerBlack",
					"PaintWoodWhite",
					"PaintWoodWhite-Endcap",
					"PaintWoodGrey",
					"PaintWoodGrey-Endcap",
					"PaintWoodGraphite",
					"PaintWoodGraphite-Endcap",
					"PaintWoodBlack",
					"PaintWoodBlack-Endcap",
					"PaintWoodRed",
					"PaintWoodRed-Endcap",
					"PaintWoodGreen",
					"PaintWoodGreen-Endcap",
					"LaminateWhite",
					"LaminateGrey",
					"LaminateGraphite",
					"LaminateBlack",
					"PaintMetalWhite",
					"PaintMetalWhite-Weld",
					"PaintMetalGrey",
					"PaintMetalGrey-Weld",
					"PaintMetalGraphite",
					"PaintMetalGraphite-Weld",
					"PaintMetalBlack",
					"PaintMetalBlack-Weld",
					"PaintMetalGreen",
					"PaintMetalGreen-Weld",
					"PaintMetalRed",
					"PaintMetalRed-Weld",
					"PaintMetalPowdercoatBlack",
					"PaintMetalPowdercoatBlack-Weld",
					"PaintMetalPowdercoatChestnut",
					"PaintMetalPowdercoatChestnut-Weld",
					"PaintMetalPowdercoatIvory",
					"PaintMetalPowdercoatIvory-Weld",
					"PaintMetalPowdercoatPetrol",
					"PaintMetalPowdercoatPetrol-Weld",
					"MetalGalvanizedSteel",
					"MetalGalvanizedSteel-Weld",
					"MetalGalvanizedZinc",
					"MetalGalvanizedZinc-Weld",
					"AtlasScrews",
					"AtlasScrews-Black",
				],
			},
			sideId: {
				$state: "top",
				$universe: ["top", "bottom", "left", "right", "round"],
			},
			count: { $state: 0, $universe: { min: 0, max: 288 } },
			chairTheme: {
				$state: "theme-teak-galvanized",
				$universe: materialThemeUniverse,
			},
		},
	},
	tableTheme: {
		$state: "theme-teak-galvanized",
		$universe: materialThemeUniverse,
	},
	areaWidth: { $state: 4 },
	areaDepth: { $state: 3 },
	flipArea: { $state: false },
	stockVersion: { $state: 0 },
	viewerMode: {
		$state: "off",
		$universe: viewerModeUniverse,
	},
	// Derived from viewerMode by the viewerVisibility rule (single writer).
	tableVisible: { $state: true },
	tableMaterialWeld: {
		$state: "WoodPineOiled",
		$universe: [
			"WoodPineOiled",
			"WoodPineOiled-Endcap",
			"WoodOakOiled",
			"WoodOakOiled-Endcap",
			"WoodTeakUntreated",
			"WoodTeakUntreated-Endcap",
			"LacquerWhite",
			"LacquerGrey",
			"LacquerGraphite",
			"LacquerBlack",
			"PaintWoodWhite",
			"PaintWoodWhite-Endcap",
			"PaintWoodGrey",
			"PaintWoodGrey-Endcap",
			"PaintWoodGraphite",
			"PaintWoodGraphite-Endcap",
			"PaintWoodBlack",
			"PaintWoodBlack-Endcap",
			"PaintWoodRed",
			"PaintWoodRed-Endcap",
			"PaintWoodGreen",
			"PaintWoodGreen-Endcap",
			"LaminateWhite",
			"LaminateGrey",
			"LaminateGraphite",
			"LaminateBlack",
			"PaintMetalWhite",
			"PaintMetalWhite-Weld",
			"PaintMetalGrey",
			"PaintMetalGrey-Weld",
			"PaintMetalGraphite",
			"PaintMetalGraphite-Weld",
			"PaintMetalBlack",
			"PaintMetalBlack-Weld",
			"PaintMetalGreen",
			"PaintMetalGreen-Weld",
			"PaintMetalRed",
			"PaintMetalRed-Weld",
			"PaintMetalPowdercoatBlack",
			"PaintMetalPowdercoatBlack-Weld",
			"PaintMetalPowdercoatChestnut",
			"PaintMetalPowdercoatChestnut-Weld",
			"PaintMetalPowdercoatIvory",
			"PaintMetalPowdercoatIvory-Weld",
			"PaintMetalPowdercoatPetrol",
			"PaintMetalPowdercoatPetrol-Weld",
			"MetalGalvanizedSteel",
			"MetalGalvanizedSteel-Weld",
			"MetalGalvanizedZinc",
			"MetalGalvanizedZinc-Weld",
			"AtlasScrews",
			"AtlasScrews-Black",
			"ScrewRoundedPhillips-M3x10mm-A",
			"ScrewRoundedPhillips-M3x10mm-A-Black",
		],
	},
	tableMaterialEndcap: {
		$state: "WoodPineOiled",
		$universe: [
			"WoodPineOiled",
			"WoodPineOiled-Endcap",
			"WoodOakOiled",
			"WoodOakOiled-Endcap",
			"WoodTeakUntreated",
			"WoodTeakUntreated-Endcap",
			"LacquerWhite",
			"LacquerGrey",
			"LacquerGraphite",
			"LacquerBlack",
			"PaintWoodWhite",
			"PaintWoodWhite-Endcap",
			"PaintWoodGrey",
			"PaintWoodGrey-Endcap",
			"PaintWoodGraphite",
			"PaintWoodGraphite-Endcap",
			"PaintWoodBlack",
			"PaintWoodBlack-Endcap",
			"PaintWoodRed",
			"PaintWoodRed-Endcap",
			"PaintWoodGreen",
			"PaintWoodGreen-Endcap",
			"LaminateWhite",
			"LaminateGrey",
			"LaminateGraphite",
			"LaminateBlack",
			"PaintMetalWhite",
			"PaintMetalWhite-Weld",
			"PaintMetalGrey",
			"PaintMetalGrey-Weld",
			"PaintMetalGraphite",
			"PaintMetalGraphite-Weld",
			"PaintMetalBlack",
			"PaintMetalBlack-Weld",
			"PaintMetalGreen",
			"PaintMetalGreen-Weld",
			"PaintMetalRed",
			"PaintMetalRed-Weld",
			"PaintMetalPowdercoatBlack",
			"PaintMetalPowdercoatBlack-Weld",
			"PaintMetalPowdercoatChestnut",
			"PaintMetalPowdercoatChestnut-Weld",
			"PaintMetalPowdercoatIvory",
			"PaintMetalPowdercoatIvory-Weld",
			"PaintMetalPowdercoatPetrol",
			"PaintMetalPowdercoatPetrol-Weld",
			"MetalGalvanizedSteel",
			"MetalGalvanizedSteel-Weld",
			"MetalGalvanizedZinc",
			"MetalGalvanizedZinc-Weld",
			undefined,
			"AtlasScrews",
			"AtlasScrews-Black",
			"ScrewRoundedPhillips-M3x10mm-A",
			"ScrewRoundedPhillips-M3x10mm-A-Black",
		],
	},
	tableMaterialScrewAtlas: {
		$state: "AtlasScrews",
		$universe: [
			"AtlasScrews",
			"AtlasScrews-Black",
			"WoodPineOiled",
			"WoodPineOiled-Endcap",
			"WoodOakOiled",
			"WoodOakOiled-Endcap",
			"WoodTeakUntreated",
			"WoodTeakUntreated-Endcap",
			"LacquerWhite",
			"LacquerGrey",
			"LacquerGraphite",
			"LacquerBlack",
			"PaintWoodWhite",
			"PaintWoodWhite-Endcap",
			"PaintWoodGrey",
			"PaintWoodGrey-Endcap",
			"PaintWoodGraphite",
			"PaintWoodGraphite-Endcap",
			"PaintWoodBlack",
			"PaintWoodBlack-Endcap",
			"PaintWoodRed",
			"PaintWoodRed-Endcap",
			"PaintWoodGreen",
			"PaintWoodGreen-Endcap",
			"LaminateWhite",
			"LaminateGrey",
			"LaminateGraphite",
			"LaminateBlack",
			"PaintMetalWhite",
			"PaintMetalWhite-Weld",
			"PaintMetalGrey",
			"PaintMetalGrey-Weld",
			"PaintMetalGraphite",
			"PaintMetalGraphite-Weld",
			"PaintMetalBlack",
			"PaintMetalBlack-Weld",
			"PaintMetalGreen",
			"PaintMetalGreen-Weld",
			"PaintMetalRed",
			"PaintMetalRed-Weld",
			"PaintMetalPowdercoatBlack",
			"PaintMetalPowdercoatBlack-Weld",
			"PaintMetalPowdercoatChestnut",
			"PaintMetalPowdercoatChestnut-Weld",
			"PaintMetalPowdercoatIvory",
			"PaintMetalPowdercoatIvory-Weld",
			"PaintMetalPowdercoatPetrol",
			"PaintMetalPowdercoatPetrol-Weld",
			"MetalGalvanizedSteel",
			"MetalGalvanizedSteel-Weld",
			"MetalGalvanizedZinc",
			"MetalGalvanizedZinc-Weld",
			"ScrewRoundedPhillips-M3x10mm-A",
			"ScrewRoundedPhillips-M3x10mm-A-Black",
		],
	},
	tableMaterialScrewPhillips: {
		$state: "ScrewRoundedPhillips-M3x10mm-A",
		$universe: [
			"ScrewRoundedPhillips-M3x10mm-A",
			"ScrewRoundedPhillips-M3x10mm-A-Black",
			"WoodPineOiled",
			"WoodPineOiled-Endcap",
			"WoodOakOiled",
			"WoodOakOiled-Endcap",
			"WoodTeakUntreated",
			"WoodTeakUntreated-Endcap",
			"LacquerWhite",
			"LacquerGrey",
			"LacquerGraphite",
			"LacquerBlack",
			"PaintWoodWhite",
			"PaintWoodWhite-Endcap",
			"PaintWoodGrey",
			"PaintWoodGrey-Endcap",
			"PaintWoodGraphite",
			"PaintWoodGraphite-Endcap",
			"PaintWoodBlack",
			"PaintWoodBlack-Endcap",
			"PaintWoodRed",
			"PaintWoodRed-Endcap",
			"PaintWoodGreen",
			"PaintWoodGreen-Endcap",
			"LaminateWhite",
			"LaminateGrey",
			"LaminateGraphite",
			"LaminateBlack",
			"PaintMetalWhite",
			"PaintMetalWhite-Weld",
			"PaintMetalGrey",
			"PaintMetalGrey-Weld",
			"PaintMetalGraphite",
			"PaintMetalGraphite-Weld",
			"PaintMetalBlack",
			"PaintMetalBlack-Weld",
			"PaintMetalGreen",
			"PaintMetalGreen-Weld",
			"PaintMetalRed",
			"PaintMetalRed-Weld",
			"PaintMetalPowdercoatBlack",
			"PaintMetalPowdercoatBlack-Weld",
			"PaintMetalPowdercoatChestnut",
			"PaintMetalPowdercoatChestnut-Weld",
			"PaintMetalPowdercoatIvory",
			"PaintMetalPowdercoatIvory-Weld",
			"PaintMetalPowdercoatPetrol",
			"PaintMetalPowdercoatPetrol-Weld",
			"MetalGalvanizedSteel",
			"MetalGalvanizedSteel-Weld",
			"MetalGalvanizedZinc",
			"MetalGalvanizedZinc-Weld",
			"AtlasScrews",
			"AtlasScrews-Black",
		],
	},
	tableMaterialWood2: {
		$state: undefined,
		$universe: [
			"WoodPineOiled",
			"WoodPineOiled-Endcap",
			"WoodOakOiled",
			"WoodOakOiled-Endcap",
			"WoodTeakUntreated",
			"WoodTeakUntreated-Endcap",
			"LacquerWhite",
			"LacquerGrey",
			"LacquerGraphite",
			"LacquerBlack",
			"PaintWoodWhite",
			"PaintWoodWhite-Endcap",
			"PaintWoodGrey",
			"PaintWoodGrey-Endcap",
			"PaintWoodGraphite",
			"PaintWoodGraphite-Endcap",
			"PaintWoodBlack",
			"PaintWoodBlack-Endcap",
			"PaintWoodRed",
			"PaintWoodRed-Endcap",
			"PaintWoodGreen",
			"PaintWoodGreen-Endcap",
			"LaminateWhite",
			"LaminateGrey",
			"LaminateGraphite",
			"LaminateBlack",
			"PaintMetalWhite",
			"PaintMetalWhite-Weld",
			"PaintMetalGrey",
			"PaintMetalGrey-Weld",
			"PaintMetalGraphite",
			"PaintMetalGraphite-Weld",
			"PaintMetalBlack",
			"PaintMetalBlack-Weld",
			"PaintMetalGreen",
			"PaintMetalGreen-Weld",
			"PaintMetalRed",
			"PaintMetalRed-Weld",
			"PaintMetalPowdercoatBlack",
			"PaintMetalPowdercoatBlack-Weld",
			"PaintMetalPowdercoatChestnut",
			"PaintMetalPowdercoatChestnut-Weld",
			"PaintMetalPowdercoatIvory",
			"PaintMetalPowdercoatIvory-Weld",
			"PaintMetalPowdercoatPetrol",
			"PaintMetalPowdercoatPetrol-Weld",
			"MetalGalvanizedSteel",
			"MetalGalvanizedSteel-Weld",
			"MetalGalvanizedZinc",
			"MetalGalvanizedZinc-Weld",
			undefined,
			"AtlasScrews",
			"AtlasScrews-Black",
			"ScrewRoundedPhillips-M3x10mm-A",
			"ScrewRoundedPhillips-M3x10mm-A-Black",
		],
	},
	tableMaterialWood: {
		$state: "WoodTeakUntreated",
		$universe: [
			"WoodPineOiled",
			"WoodPineOiled-Endcap",
			"WoodOakOiled",
			"WoodOakOiled-Endcap",
			"WoodTeakUntreated",
			"WoodTeakUntreated-Endcap",
			"LacquerWhite",
			"LacquerGrey",
			"LacquerGraphite",
			"LacquerBlack",
			"PaintWoodWhite",
			"PaintWoodWhite-Endcap",
			"PaintWoodGrey",
			"PaintWoodGrey-Endcap",
			"PaintWoodGraphite",
			"PaintWoodGraphite-Endcap",
			"PaintWoodBlack",
			"PaintWoodBlack-Endcap",
			"PaintWoodRed",
			"PaintWoodRed-Endcap",
			"PaintWoodGreen",
			"PaintWoodGreen-Endcap",
			"LaminateWhite",
			"LaminateGrey",
			"LaminateGraphite",
			"LaminateBlack",
			"PaintMetalWhite",
			"PaintMetalWhite-Weld",
			"PaintMetalGrey",
			"PaintMetalGrey-Weld",
			"PaintMetalGraphite",
			"PaintMetalGraphite-Weld",
			"PaintMetalBlack",
			"PaintMetalBlack-Weld",
			"PaintMetalGreen",
			"PaintMetalGreen-Weld",
			"PaintMetalRed",
			"PaintMetalRed-Weld",
			"PaintMetalPowdercoatBlack",
			"PaintMetalPowdercoatBlack-Weld",
			"PaintMetalPowdercoatChestnut",
			"PaintMetalPowdercoatChestnut-Weld",
			"PaintMetalPowdercoatIvory",
			"PaintMetalPowdercoatIvory-Weld",
			"PaintMetalPowdercoatPetrol",
			"PaintMetalPowdercoatPetrol-Weld",
			"MetalGalvanizedSteel",
			"MetalGalvanizedSteel-Weld",
			"MetalGalvanizedZinc",
			"MetalGalvanizedZinc-Weld",
			undefined,
			"AtlasScrews",
			"AtlasScrews-Black",
			"ScrewRoundedPhillips-M3x10mm-A",
			"ScrewRoundedPhillips-M3x10mm-A-Black",
		],
	},
	tableMaterialMetal: {
		$state: "MetalGalvanizedSteel",
		$universe: [
			"WoodPineOiled",
			"WoodPineOiled-Endcap",
			"WoodOakOiled",
			"WoodOakOiled-Endcap",
			"WoodTeakUntreated",
			"WoodTeakUntreated-Endcap",
			"LacquerWhite",
			"LacquerGrey",
			"LacquerGraphite",
			"LacquerBlack",
			"PaintWoodWhite",
			"PaintWoodWhite-Endcap",
			"PaintWoodGrey",
			"PaintWoodGrey-Endcap",
			"PaintWoodGraphite",
			"PaintWoodGraphite-Endcap",
			"PaintWoodBlack",
			"PaintWoodBlack-Endcap",
			"PaintWoodRed",
			"PaintWoodRed-Endcap",
			"PaintWoodGreen",
			"PaintWoodGreen-Endcap",
			"LaminateWhite",
			"LaminateGrey",
			"LaminateGraphite",
			"LaminateBlack",
			"PaintMetalWhite",
			"PaintMetalWhite-Weld",
			"PaintMetalGrey",
			"PaintMetalGrey-Weld",
			"PaintMetalGraphite",
			"PaintMetalGraphite-Weld",
			"PaintMetalBlack",
			"PaintMetalBlack-Weld",
			"PaintMetalGreen",
			"PaintMetalGreen-Weld",
			"PaintMetalRed",
			"PaintMetalRed-Weld",
			"PaintMetalPowdercoatBlack",
			"PaintMetalPowdercoatBlack-Weld",
			"PaintMetalPowdercoatChestnut",
			"PaintMetalPowdercoatChestnut-Weld",
			"PaintMetalPowdercoatIvory",
			"PaintMetalPowdercoatIvory-Weld",
			"PaintMetalPowdercoatPetrol",
			"PaintMetalPowdercoatPetrol-Weld",
			"MetalGalvanizedSteel",
			"MetalGalvanizedSteel-Weld",
			"MetalGalvanizedZinc",
			"MetalGalvanizedZinc-Weld",
			"AtlasScrews",
			"AtlasScrews-Black",
			"ScrewRoundedPhillips-M3x10mm-A",
			"ScrewRoundedPhillips-M3x10mm-A-Black",
		],
	},
});
/* eslint-enable @animech/axiom/no-references-in-top-level-values */
